import { VetoHistoryEntry } from '../../models/VetoHistory';

/**
 * The veto simulator's brain, kept free of React so it can be reasoned about (and tested) alone.
 *
 * Opponent model: a Plackett-Luce strength per map, fitted to the opponent's past ban (or pick)
 * decisions. P(they ban m | maps still in play S) = w[m] / sum(w[S]). Because it conditions on
 * what is left, "Nuke first, but Anubis when Nuke is gone" falls out without special cases, and
 * a pool change (Cache in, Overpass out) needs no remapping.
 *
 * Each past veto is weighted by how many of today's five made it and how recent it is. Backtested
 * on S31-S32 (predicting each ban from earlier vetoes only): 69% on the three-ban step against 65%
 * for the team's own history unweighted by lineup, 61% for "their most-banned maps", 40% random.
 */

export type Side = 'us' | 'them';
export type Format = 'bo1' | 'bo1ligaen' | 'bo3';
export type Action = 'ban' | 'pick';

export interface FormatStep { first: boolean; action: Action }

/**
 * Steps in order; `first` is the team that opens the veto. A bo1 plays the picked map, or the
 * last map standing when nobody picks; a bo3 plays both picks and then the decider.
 * Checked against S33 prod vetoes per division.
 */
export const FORMATS: Record<Format, { label: string; series: 'bo1' | 'bo3'; steps: FormatStep[] }> = {
    // 1div-5div bo1: opener bans three, the other team picks one of the last four, opener picks side.
    // The bans are there to shape which four maps the other team gets to pick from.
    bo1: {
        label: 'Bo1 · ban 3, pick 1',
        series: 'bo1',
        steps: [
            { first: true, action: 'ban' }, { first: true, action: 'ban' }, { first: true, action: 'ban' },
            { first: false, action: 'pick' },
        ],
    },
    // Ligaen and liga-kval bo1: opener bans 2, the other bans 3, opener bans the last, survivor is played.
    bo1ligaen: {
        label: 'Bo1 · ligaen 2-3-1',
        series: 'bo1',
        steps: [
            { first: true, action: 'ban' }, { first: true, action: 'ban' },
            { first: false, action: 'ban' }, { first: false, action: 'ban' }, { first: false, action: 'ban' },
            { first: true, action: 'ban' },
        ],
    },
    // Bo3: ban, ban, pick, pick, ban, ban, decider.
    bo3: {
        label: 'Bo3',
        series: 'bo3',
        steps: [
            { first: true, action: 'ban' }, { first: false, action: 'ban' },
            { first: true, action: 'pick' }, { first: false, action: 'pick' },
            { first: false, action: 'ban' }, { first: true, action: 'ban' },
        ],
    },
};

/** Fallback when no veto this season names the pool. Season 33. */
export const DEFAULT_POOL = ['Ancient', 'Anubis', 'Cache', 'Dust2', 'Inferno', 'Mirage', 'Nuke'];

/** The current pool as the most recent full veto (all seven maps named) this season saw it. */
export const currentPool = (histories: VetoHistoryEntry[][], season: number): string[] => {
    const latest = histories.flat()
        .filter(e => e.season === season && e.pool.length === 7)
        .sort((a, b) => (b.matchTime ?? '').localeCompare(a.matchTime ?? ''))[0];
    return latest ? [...latest.pool].sort() : DEFAULT_POOL;
};

// ---------- Weights ----------

const OVERLAP_WEIGHT: Record<number, number> = { 5: 1, 4: 0.8, 3: 0.6, 2: 0.25 };

const recency = (e: VetoHistoryEntry, season: number): number => {
    const age = season - e.season;
    return age <= 0 ? 1 : age === 1 ? 0.6 : 0.3;
};

/**
 * How much one past veto says about today's lineup: only who made it, never which team id it sat
 * under. An org's team id can carry a different squad from an earlier season (Unsigned Vultures
 * under Unsigned Vibes' id), and backtesting agreed: giving the team's own vetoes a floor even
 * with 0-1 of today's players cost accuracy (63.7% vs 64.6% on S31-S32 bans).
 *
 * `include` is the viewer's override from the "Hvorfor?" list: false drops the veto, true counts
 * it even when the lineup overlap alone would not.
 */
export const vetoWeight = (e: VetoHistoryEntry, season: number, include?: boolean): number => {
    if (include === false) return 0;
    const auto = (OVERLAP_WEIGHT[Math.min(e.overlap, 5)] ?? 0) * recency(e, season);
    return include === true ? Math.max(auto, 0.25 * recency(e, season)) : auto;
};

/** matchId -> include (true) / exclude (false); absent means the lineup-overlap default. */
export type Overrides = Record<string, boolean>;

// ---------- Plackett-Luce ----------

interface Decision { available: string[]; chosen: string; weight: number }

/** Every `action` decision the subject made, with the maps still in play at that moment. */
const decisionsFor = (history: VetoHistoryEntry[], action: Action, season: number, overrides: Overrides): Decision[] => {
    const out: Decision[] = [];
    for (const e of history) {
        const weight = vetoWeight(e, season, overrides[e.matchId]);
        if (weight <= 0) continue;
        const avail = new Set(e.pool);
        for (const s of e.steps) {
            if (s.kind === 'side') continue;
            if (s.actor === 'subject' && s.kind === action && avail.has(s.map)) {
                out.push({ available: Array.from(avail), chosen: s.map, weight });
            }
            avail.delete(s.map);
        }
    }
    return out;
};

export type Strengths = Record<string, number>;

/**
 * MM iterations (Hunter 2004) with a pseudo-count of `alpha` pulling every map towards 1, so a
 * map with no history keeps a fair share instead of zero. Normalised to geometric mean 1.
 */
const fitStrengths = (decisions: Decision[], maps: string[], alpha = 1, iterations = 40): Strengths => {
    const all = new Set(maps);
    decisions.forEach(d => d.available.forEach(m => all.add(m)));
    let w: Strengths = {};
    all.forEach(m => { w[m] = 1; });
    const wins: Strengths = {};
    decisions.forEach(d => { wins[d.chosen] = (wins[d.chosen] ?? 0) + d.weight; });

    for (let it = 0; it < iterations; it++) {
        const cur = w;
        const den: Strengths = {};
        for (const d of decisions) {
            const z = d.available.reduce((s, m) => s + cur[m], 0);
            d.available.forEach(m => { den[m] = (den[m] ?? 0) + d.weight / z; });
        }
        const next: Strengths = {};
        all.forEach(m => { next[m] = ((wins[m] ?? 0) + alpha) / ((den[m] ?? 0) + alpha); });
        const logMean = Object.values(next).reduce((s, x) => s + Math.log(x), 0) / all.size;
        const g = Math.exp(logMean);
        const normalised: Strengths = {};
        all.forEach(m => { normalised[m] = next[m] / g; });
        w = normalised;
    }
    return w;
};

export interface TeamModel {
    ban: Strengths;
    pick: Strengths;
    /** Weighted evidence behind each model; zero means the numbers are just the prior. */
    banEvidence: number;
    pickEvidence: number;
    /** The vetoes that carried any weight, newest first. */
    used: { entry: VetoHistoryEntry; weight: number }[];
    /** Every veto found, counted or not, with the weight it would get without an override. */
    all: { entry: VetoHistoryEntry; weight: number; autoWeight: number }[];
}

export const buildTeamModel = (
    history: VetoHistoryEntry[], pool: string[], season: number, overrides: Overrides = {},
): TeamModel => {
    const bans = decisionsFor(history, 'ban', season, overrides);
    const picks = decisionsFor(history, 'pick', season, overrides);
    const all = history.map(entry => ({
        entry,
        weight: vetoWeight(entry, season, overrides[entry.matchId]),
        autoWeight: vetoWeight(entry, season),
    }));
    return {
        ban: fitStrengths(bans, pool),
        pick: fitStrengths(picks, pool),
        banEvidence: bans.reduce((s, d) => s + d.weight, 0),
        pickEvidence: picks.reduce((s, d) => s + d.weight, 0),
        used: all.filter(u => u.weight > 0).map(({ entry, weight }) => ({ entry, weight })),
        all,
    };
};

/** P(each available map is chosen next). */
export const choiceProbabilities = (strengths: Strengths, available: string[]): Record<string, number> => {
    const z = available.reduce((s, m) => s + (strengths[m] ?? 1), 0);
    const out: Record<string, number> = {};
    available.forEach(m => { out[m] = (strengths[m] ?? 1) / z; });
    return out;
};

// ---------- Veto state and search ----------

export interface Move { side: Side; action: Action; map: string }

export interface VetoSetup {
    format: Format;
    /** Who opens the veto. */
    opener: Side;
    pool: string[];
    /**
     * What each map is worth to us, 0..1. With objective 'win' it is our chance of winning it; with
     * 'pref' it is a score from the viewer's own ranked map pool (see preferenceValues).
     */
    winChance: Record<string, number>;
    objective?: 'win' | 'pref';
    them: TeamModel;
}

/** Who acts next and how, or null once the veto is complete. */
export const nextStep = (setup: VetoSetup, moves: Move[]): { side: Side; action: Action } | null => {
    const step = FORMATS[setup.format].steps[moves.length];
    if (!step) return null;
    const other: Side = setup.opener === 'us' ? 'them' : 'us';
    return { side: step.first ? setup.opener : other, action: step.action };
};

export const remainingMaps = (pool: string[], moves: Move[]): string[] =>
    pool.filter(m => !moves.some(mv => mv.map === m));

/**
 * The map a finished bo1 is played on, or a bo3's decider: the bo1 pick when the format has one,
 * otherwise the last map standing. Undefined while the veto is still open.
 */
export const playedMap = (setup: VetoSetup, moves: Move[]): string | undefined => {
    if (nextStep(setup, moves)) return undefined;
    if (FORMATS[setup.format].series === 'bo1') {
        const pick = moves.find(m => m.action === 'pick')?.map;
        if (pick) return pick;
    }
    const left = remainingMaps(setup.pool, moves);
    return left.length === 1 ? left[0] : undefined;
};

/** The format a fixture most likely uses, from its division. Checked on S33 prod vetoes. */
export const defaultFormat = (division: string): Format => {
    if (/playoff/i.test(division)) return 'bo3';
    if (/^ligaen$|kval/i.test(division)) return 'bo1ligaen';
    return 'bo1';
};

/** Chance we win a best-of-three given each map's win chance, in play order. */
const seriesWin = (a: number, b: number, c: number): number =>
    a * b + a * (1 - b) * c + (1 - a) * b * c;

export interface Outcome {
    /** Our expected chance of winning the match (bo1: the map; bo3: the series). */
    win: number;
    /** Probability each map ends up as the played map (bo1) or the decider (bo3). */
    finalMap: Record<string, number>;
}

/**
 * Expectimax over the rest of the veto. On our turns we take the move that maximises our win
 * chance; on theirs we follow their fitted tendencies, not a worst case, because the point is to
 * plan around what they actually do. At most 7! leaves, so no pruning is needed.
 */
export const evaluate = (setup: VetoSetup, moves: Move[]): Outcome => {
    const memo = new Map<string, Outcome>();
    const pickOf = (ms: Move[], side: Side) => ms.find(m => m.action === 'pick' && m.side === side)?.map;

    const rec = (ms: Move[]): Outcome => {
        const left = remainingMaps(setup.pool, ms);
        const step = nextStep(setup, ms);
        if (!step || left.length <= 1) {
            const last = playedMap(setup, ms) ?? left[0];
            const p = (m?: string) => (m ? setup.winChance[m] ?? 0.5 : 0.5);
            if (FORMATS[setup.format].series === 'bo1') {
                return { win: p(last), finalMap: last ? { [last]: 1 } : {} };
            }
            // Bo3 map order: opener's pick, then the other's, then the decider.
            const openerPick = pickOf(ms, setup.opener);
            const otherPick = pickOf(ms, setup.opener === 'us' ? 'them' : 'us');
            // A preference score is not a probability, so a series is scored as its three maps' mean.
            const win = setup.objective === 'pref'
                ? (p(openerPick) + p(otherPick) + p(last)) / 3
                : seriesWin(p(openerPick), p(otherPick), p(last));
            return { win, finalMap: last ? { [last]: 1 } : {} };
        }

        const key = ms.map(m => `${m.side[0]}${m.action[0]}${m.map}`).join('|');
        const hit = memo.get(key);
        if (hit) return hit;

        let result: Outcome;
        if (step.side === 'us') {
            let best: Outcome | null = null;
            for (const map of left) {
                const o = rec([...ms, { side: 'us', action: step.action, map }]);
                if (!best || o.win > best.win) best = o;
            }
            result = best!;
        } else {
            const probs = choiceProbabilities(step.action === 'ban' ? setup.them.ban : setup.them.pick, left);
            const acc: Outcome = { win: 0, finalMap: {} };
            for (const map of left) {
                const o = rec([...ms, { side: 'them', action: step.action, map }]);
                acc.win += probs[map] * o.win;
                Object.entries(o.finalMap).forEach(([m, p]) => {
                    acc.finalMap[m] = (acc.finalMap[m] ?? 0) + probs[map] * p;
                });
            }
            result = acc;
        }
        memo.set(key, result);
        return result;
    };
    return rec(moves);
};

/** What each of our available moves leads to, best first. */
export const rankOurMoves = (setup: VetoSetup, moves: Move[]): { map: string; outcome: Outcome }[] => {
    const step = nextStep(setup, moves);
    if (!step || step.side !== 'us') return [];
    return remainingMaps(setup.pool, moves)
        .map(map => ({ map, outcome: evaluate(setup, [...moves, { side: 'us', action: step.action, map }]) }))
        .sort((a, b) => b.outcome.win - a.outcome.win);
};

/**
 * Scores for the viewer's own map pool: played maps from 1.0 (first choice) down to 0.6 (last),
 * maps they don't play 0. The gap to 0 is deliberately bigger than any gap inside the pool, so the
 * search first makes sure the veto lands on a map they play and only then chases their favourite.
 */
export const preferenceValues = (order: string[], off: string[]): Record<string, number> => {
    const played = order.filter(m => !off.includes(m));
    const out: Record<string, number> = {};
    order.forEach(m => { out[m] = 0; });
    played.forEach((m, i) => { out[m] = played.length > 1 ? 1 - (0.4 * i) / (played.length - 1) : 1; });
    return out;
};

/** Our default chance on a map: each side's record shrunk towards 50%, then set against each other. */
export const defaultWinChance = (
    us: { won: number; played: number } | undefined,
    them: { won: number; played: number } | undefined,
): number => {
    const rate = (r?: { won: number; played: number }) => ((r?.won ?? 0) + 2.5) / ((r?.played ?? 0) + 5);
    const p = 0.5 + (rate(us) - rate(them)) / 2;
    return Math.min(0.9, Math.max(0.1, p));
};
